import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';

const Card = ({ label, value, sub, color = 'var(--orange)' }) => (
  <div style={{ background: 'var(--white)', borderRadius: 16, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', borderTop: `3px solid ${color}` }}>
    <div style={{ fontSize: 13, color: 'var(--gray)', fontWeight: 600, marginBottom: 8 }}>{label}</div>
    <div style={{ fontSize: 28, fontWeight: 900, color: 'var(--dark)' }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: 'var(--gray)', marginTop: 4 }}>{sub}</div>}
  </div>
);

export default function DashboardTab({ user, centerData, centers }) {
  const [stats, setStats]   = useState({ todaysJobs: 0, inProgress: 0, completedToday: 0, todaysRevenue: 0, avgRating: 0 });
  const [jobs, setJobs]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!centerData?.id) { setLoading(false); return; }
    loadStats();
  }, [centerData]);

  const loadStats = async () => {
    try {
      const start = new Date(); start.setHours(0,0,0,0);
      const end   = new Date(); end.setHours(23,59,59,999);
      const q = query(
        collection(db, 'washRequests'),
        where('serviceCenterId', '==', centerData.id),
        where('scheduledAt', '>=', Timestamp.fromDate(start)),
        where('scheduledAt', '<=', Timestamp.fromDate(end))
      );
      const snap = await getDocs(q);
      const jobList = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setJobs(jobList.slice(0, 5));
      const completed = jobList.filter(j => j.centerStatus === 'completed');
      const inProg    = jobList.filter(j => ['in_service', 'checked_in'].includes(j.centerStatus));
      const revenue   = completed.reduce((sum, j) => sum + (j.serviceCenterPaidAmount || 0), 0);
      setStats({
        todaysJobs:      jobList.length,
        inProgress:      inProg.length,
        completedToday:  completed.length,
        todaysRevenue:   revenue,
        avgRating:       centerData.ratingAverage || 0,
      });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (!centerData) return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <div style={{ fontSize: 40, marginBottom: 16 }}>🏢</div>
      <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Welcome to CocoWash Service Center Portal</h2>
      <p style={{ color: 'var(--gray)' }}>Your account is pending approval. You will receive an email once approved.</p>
    </div>
  );

  return (
    <div style={{ padding: 32 }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 24, fontWeight: 900 }}>Dashboard</h1>
        <p style={{ color: 'var(--gray)', marginTop: 4 }}>{centerData.businessName} — {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16, marginBottom: 32 }}>
        <Card label="Today's Jobs"     value={stats.todaysJobs}     color="var(--orange)" />
        <Card label="In Progress"      value={stats.inProgress}     color="var(--blue)" />
        <Card label="Completed Today"  value={stats.completedToday} color="var(--green)" />
        <Card label="Today's Revenue"  value={`$${stats.todaysRevenue.toFixed(2)}`} color="var(--green)" />
        <Card label="Avg Rating"       value={stats.avgRating > 0 ? stats.avgRating.toFixed(1) + ' ⭐' : 'No ratings'} color="var(--purple)" />
        <Card label="Locations"        value={centers?.length || 1} color="var(--gray)" />
      </div>

      {/* Recent jobs */}
      <div style={{ background: 'var(--white)', borderRadius: 16, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <h3 style={{ fontSize: 16, fontWeight: 800, marginBottom: 16 }}>Today's Incoming Jobs</h3>
        {loading ? <p style={{ color: 'var(--gray)' }}>Loading...</p> : jobs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--gray)' }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>🔧</div>
            <p>No jobs scheduled for today</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Time', 'Service', 'Status', 'Amount'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, fontWeight: 700, color: 'var(--gray)', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {jobs.map(job => (
                <tr key={job.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '12px', fontSize: 13 }}>{job.scheduledAt?.toDate?.()?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) || '—'}</td>
                  <td style={{ padding: '12px', fontSize: 13, fontWeight: 600 }}>{(job.selectedServices || []).join(', ') || '—'}</td>
                  <td style={{ padding: '12px' }}>
                    <span style={{ background: job.centerStatus === 'completed' ? '#f0fdf4' : job.centerStatus === 'in_service' ? '#eff6ff' : '#fff7ed', color: job.centerStatus === 'completed' ? 'var(--green)' : job.centerStatus === 'in_service' ? 'var(--blue)' : 'var(--orange)', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>
                      {job.centerStatus || 'scheduled'}
                    </span>
                  </td>
                  <td style={{ padding: '12px', fontSize: 13, fontWeight: 700 }}>${(job.quotedCenterAmount || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}